import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import TrueImpactDividerModal from 'shared/design-system/components/TrueImpactDividerModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import {
  createNewReport,
  ReportingType,
  SuccessResponse,
} from '../ReportsBeta/NewReport/api';
import { useGoToReport } from '../ReportsBeta/NewReport/useGoToReport';
import { UserProgram } from '../ProgramsAndReports/types';
import SuccessMessage from '../ReportsBeta/NewReport/SuccessMessage';

interface Props {
  open: boolean;
  onClose: () => void;
  program: UserProgram | undefined;
  year: number;
  onCreated?: () => void;
}

type Choice = Extract<ReportingType, 'LIGHT' | 'FULL'>;

const AddReportHeader: React.FC = () => (
  <StyledHeader>
    <TrueImpactText variant="heading" className="header-title">
      What kind of report?
    </TrueImpactText>
    <TrueImpactText variant="body-small" color="greyDark">
      Both types create a universal report you can share with any donor — no
      need to recreate it for each.
    </TrueImpactText>
  </StyledHeader>
);

const AddReportModal: React.FC<Props> = ({
  open,
  onClose,
  program,
  year,
  onCreated,
}) => {
  const client = useClient();
  const goToReport = useGoToReport();
  const [choice, setChoice] = useState<Choice>('LIGHT');
  const [submittedProgram, setSubmittedProgram] = useState<
    UserProgram | undefined
  >(undefined);
  const [newReport, makeNewReportRequest, setNewReport] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >(undefined, true);

  useEffect(() => {
    if (!open) {
      setChoice('LIGHT');
      setSubmittedProgram(undefined);
      setNewReport(requestInit());
    }
  }, [open, setNewReport]);

  const submitting = newReport.type === 'REQUEST_START';
  const successPayload =
    newReport.type === 'REQUEST_SUCCESS' ? newReport.data.newReport : undefined;
  const successId =
    successPayload && successPayload.success ? successPayload.id : undefined;
  const mutationError =
    newReport.type === 'REQUEST_ERROR'
      ? newReport.error.response?.data?.message ||
        'Something went wrong creating your report. Please try again.'
      : successPayload && !successPayload.success
      ? successPayload.error ||
        'Something went wrong creating your report. Please try again.'
      : undefined;

  const isSuccess = newReport.type === 'REQUEST_SUCCESS' && Boolean(successId);
  const activeProgram = submittedProgram || program;

  const handleClose = useCallback(() => {
    onClose();
    if (isSuccess) onCreated?.();
  }, [onClose, onCreated, isSuccess]);

  const handleGoToReport = useCallback(() => {
    if (!successId || !activeProgram) return;
    goToReport({
      reportId: successId,
      programId: activeProgram.program.dbId,
      programName: activeProgram.program.name,
      reportingType: choice,
    });
  }, [successId, activeProgram, choice, goToReport]);

  const handleSubmit = useCallback(() => {
    if (!client || !program || submitting) return;
    setSubmittedProgram(program);
    const programArg = {
      Id: program.program.dbId,
      name: program.program.name,
      managerId: program.program.managerId ?? 0,
    };
    makeNewReportRequest(
      createNewReport(client, programArg, year, undefined, undefined, choice),
    );
  }, [client, program, submitting, year, choice, makeNewReportRequest]);

  const options = useMemo(
    () => [
      {
        value: 'LIGHT' as const,
        title: 'Impact Snapshot',
        badge: 'Most start here · ~10 min',
        description:
          'Reach, location, demographics, and budget. Can be upgraded to a full report at any time — your data carries over.',
      },
      {
        value: 'FULL' as const,
        title: 'Full report',
        description:
          'Everything in a snapshot, plus indicators, outcomes, and narratives. Maximum visibility with donors.',
      },
    ],
    [],
  );

  return (
    <TrueImpactDividerModal
      open={open}
      onClose={handleClose}
      maxWidth="750px"
      header={isSuccess ? undefined : <AddReportHeader />}
      footer={
        isSuccess ? (
          <>
            <TrueImpactButton variant="secondary-large" onClick={handleClose}>
              No thanks
            </TrueImpactButton>
            <TrueImpactButton
              variant="primary-large"
              onClick={handleGoToReport}
            >
              Yes, take me there
            </TrueImpactButton>
          </>
        ) : (
          <>
            <TrueImpactButton
              variant="secondary-large"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </TrueImpactButton>
            <TrueImpactButton
              variant="primary-large"
              isLoading={submitting}
              disabled={!program || submitting}
              onClick={handleSubmit}
            >
              Create report
            </TrueImpactButton>
          </>
        )
      }
    >
      {isSuccess ? (
        <SuccessMessage
          heading="Your report was successfully created!"
          subHeading={{
            one: 'Would you like to begin updating your report now?',
          }}
        />
      ) : (
        <StyledContent>
          {options.map(opt => {
            const selected = choice === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`option${selected ? ' selected' : ''}`}
                onClick={() => setChoice(opt.value)}
                disabled={submitting}
              >
                <div className="option-title">
                  <TrueImpactText variant="body-bold" color="blackBase">
                    {opt.title}
                  </TrueImpactText>
                  {opt.badge && (
                    <span className="badge">
                      <TrueImpactText
                        variant="body-small-bold"
                        color="ctaPrimary"
                      >
                        {opt.badge}
                      </TrueImpactText>
                    </span>
                  )}
                </div>
                <TrueImpactText variant="body-small" color="greyDark">
                  {opt.description}
                </TrueImpactText>
              </button>
            );
          })}
          {mutationError && (
            <TrueImpactText variant="body-small" color="critical">
              {mutationError}
            </TrueImpactText>
          )}
        </StyledContent>
      )}
    </TrueImpactDividerModal>
  );
};

const StyledHeader = styled.div`
  display: contents;
`;

const StyledContent = styled.div`
  display: contents;
  .option {
    text-align: left;
    background: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.greyLight};
    border-radius: ${SPACERS.tiny};
    padding: ${SPACERS.medium};
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
    font: inherit;
  }
  .option:hover:not(:disabled) {
    border-color: ${COLORS.ctaPrimary};
  }
  .option.selected {
    border-color: ${COLORS.ctaPrimary};
    background: ${COLORS.ctaPrimaryBackground};
    box-shadow: inset 0 0 0 1px ${COLORS.ctaPrimary};
  }
  .option:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  .option-title {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    flex-wrap: wrap;
  }
`;

export default AddReportModal;
